import { createObservable } from '@/shared/utils/create-observable'
import { Validation } from '@/shared/forms/validation'
import Vue from 'vue'
import { StringMap } from '@/shared/utils/map'
import { RuleDecl } from 'vue/types/options'
import { vuelidateMapping } from '@/shared/forms/vuelidate-mapping'
import { FormGroupImpl } from '@/shared/forms/form-group-impl'
import { FormState } from '@/shared/forms/validation-properties'
import { loggerFor } from '@/shared/utils/logger'
import { copy } from '@/shared/utils/deepmerge'
import { BasePropertyBlueprint, mergeAll, PropertyMapping } from '@hakla/typed-forms'

export class TypedForm<Model, Form = Model> {
  public dirty = false
  public value: Form
  private logger = loggerFor('TypedForm')
  private doNotValidate = false

  public get pristine(): boolean {
    return !this.dirty
  }

  public get invalid(): boolean {
    return this.state.$invalid || false
  }

  public get state(): FormState<Form> {
    let state: FormState<Form> = {
      form: {
        value: {},
      },
    } as any

    if (this.vue.$v !== undefined && this.vue.$v.form !== undefined) {
      state = (this.vue.$v as unknown) as FormState<Form>
    } else {
      this.logger.error('Missing form state of vuelidate, did you set the validations?')
    }

    return state
  }

  private get properties() {
    return this.mapping.properties
  }

  constructor(private mapping: PropertyMapping<Model, Form>, private vue: Vue) {
    this.value = {} as any

    this.doNotValidate = true

    const createProperty = (
      property: BasePropertyBlueprint<Model, Form>,
      formValue: any,
      value: any
    ) => {
      if (typeof formValue[property.name] === 'undefined') {
        if (property.type === 'nested') {
          // Nested property
          formValue[property.name] = {}

          value[property.name] = property.initialValue

          createFormObject((property as any).properties, formValue[property.name])
        } else if (property.type === 'array') {
          formValue[property.name] = []

          value[property.name] = property.initialValue

          createFormObject(property.properties as any, formValue[property.name].$each)
        } else {
          formValue[property.name] = undefined

          value[property.name] = property.initialValue

          // Watch programmatic changes and update the touched attribute of the form control
          createObservable(formValue, property.name, name => {
            if (vue.$v !== undefined) {
              const form: any = vue.$v.form
              const field = form.value[name]

              if (isValidation(field)) {
                field.$touch()
              }
            }

            this.dirty = true
          })

          if (value[property.name] !== undefined) {
            // @ts-ignore
            formValue[property.name] = value[property.name]
          }
        }
      }
    }

    function createFormObject(
      properties: Array<BasePropertyBlueprint<Model, Form>>,
      formValue: any
    ) {
      const value: Partial<Form> = {}

      for (const property of properties) {
        createProperty(property, formValue, value)
      }
    }

    createFormObject(this.properties, this.value)

    this.doNotValidate = false
  }

  public deserialize(value: Model): this {
    // Copy the original object because we cannot mutate the state directly (e.g. if it comes from vuex)
    const deserialized: Form = copy(this.mapping.deserialize(value)) as Form

    mergeAll(this.mapping, this.value, deserialized)

    return this
  }

  /**
   * Returns the vuelidate validation object or an empty object if it doesn't exist
   * @param name
   */
  public field<Key extends keyof Form>(name: Key): Validation<Form[Key]> {
    return (
      this.state.form.value[name] ||
      ({
        $dirty: false,
        $invalid: false,
      } as any)
    )
  }

  public group() {
    return new FormGroupImpl(this.state.form.value, this.value)
  }

  public patch(patchValue: Partial<Form>): this {
    Object.entries(patchValue).forEach(([key, value]) => {
      // @ts-ignore
      this.value[key] = value
    })

    return this
  }

  public reset(value?: Model): void {
    this.doNotValidate = true

    if (value !== undefined) {
      this.deserialize(value)
    } else {
      this.properties.forEach(property => unset(property, this.value))
    }

    if (this.vue.$v !== undefined) {
      this.vue.$v.$reset()
    }

    this.dirty = false
    this.doNotValidate = false
  }

  public serialize(original?: Model): Model {
    return this.mapping.serialize(original || {}, this.value) as Model
  }

  public validations(): RuleDecl {
    const mapped: StringMap<any> = vuelidateMapping(this.mapping)

    return {
      form: {
        value: mapped,
      },
    }
  }
}

function unset<U>(property: BasePropertyBlueprint<any, U>, a: U) {
  if (property.type === 'simple') {
    // Simple property
    // @ts-ignore
    a[property.name] = undefined
  } else {
    // Nested property
    for (const nestedProperty of property.properties) {
      unset(nestedProperty, a[property.name] as any)
    }
  }
}

function isValidation<T>(value: any): value is Validation {
  return value !== undefined && '$invalid' in value
}
